import { Injectable } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { Observable, forkJoin, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import {
  BackendRequestTypes,
  FrontendRequestTypes,
  RosterMember,
  SupportRequest,
  SupportRequestFormData,
  SupportRequestSlackPayload,
  SupportRequestType
} from '../interfaces/support-request.interface'

/**
 * Support team rosters with Slack IDs
 *
 * Assignment rotation:
 * - Uses time-based rotation (day of year)
 * - Same person assigned for all requests on the same day
 * - Automatically rotates daily for fair distribution
 * - Frontend requests rotate through FE_ROSTER
 * - Backend requests rotate through BE_ROSTER
 *
 * Slack user IDs are used for @mentions in Slack messages.
 *
 * HOW TO FIND SLACK USER IDs:
 * 1. In Slack, click on a user's profile
 * 2. Click "More" (three dots)
 * 3. Click "Copy member ID"
 * 4. User IDs start with 'U' (e.g., U024BP7V8AX)
 *
 * @todo: Replace placeholder Slack IDs with actual team member IDs
 */
const FE_ROSTER: RosterMember[] = [
  // { name: 'Eslam', slackId: 'U062Q51CUA2' },
  // { name: 'Hafsa', slackId: 'U068LJGAYUD' },
  // { name: 'Mostafa', slackId: 'U087E7KFT2P' },
  // { name: 'Damarawy', slackId: 'U024BGWCKHQ' },
  { name: 'Ashraf', slackId: 'U083ZSRHW87' }
]

const BE_ROSTER: RosterMember[] = [
  // { name: 'Gehad', slackId: 'U06LSGPPQP5' },
  // { name: 'Hatem', slackId: 'U01USSRSU8N' },
  // { name: 'Islam', slackId: 'U080H6D2PPC' },
  { name: 'Omar', slackId: 'U0A7YPW5BEF' }
]

const INTEGRATION_TEAM: RosterMember[] = [
  { name: 'Hatem', slackId: 'U01USSRSU8N' },
  { name: 'Alaa', slackId: 'U03TG6TBQKY' },
  { name: 'Mostafa', slackId: 'U087E7KFT2P' }
]

/**
 * Support Request Type Labels
 * Human-readable labels for support request types
 */
export const SupportRequestTypeLabels: Record<SupportRequestType, string> = {
  [SupportRequestType.CUSTOM_BADGE]: 'Custom Badge Design',
  [SupportRequestType.CUSTOM_EMAIL]: 'Custom Email Templates',
  [SupportRequestType.CONDITIONAL_FIELDS]: 'Conditional Fields Setup',
  [SupportRequestType.CUSTOM_REPORTS]: 'Custom Reports/Exports',
  [SupportRequestType.PAYMENT_GATEWAY]: 'Payment Gateway Integration',
  [SupportRequestType.THIRD_PARTY_APPS]: 'Third-Party App Integrations',
  [SupportRequestType.CUSTOM_LANDING_PAGE]: 'Custom Landing Page Modifications',
  [SupportRequestType.SMS_NOTIFICATIONS]: 'SMS Notifications Setup',
  [SupportRequestType.MULTI_LANGUAGE]: 'Multi-Language Support',
  [SupportRequestType.OTHER]: 'Other'
}

/**
 * Support Request Service
 * Handles all support request business logic including:
 * - Creating and managing support requests
 * - Roster rotation for team assignment
 * - Slack notifications
 * - Event options updates
 */
@Injectable({
  providedIn: 'root'
})
export class SupportRequestService {
  constructor(private integrationService: IntegrationService, private eventService: EventService, private authService: AuthService) {}

  /**
   * Creates a new support request, sends Slack notification, and updates event options
   * @param event Event object
   * @param formData Support request form data
   * @returns Observable<SupportRequest>
   */
  createSupportRequest(event: ServerRepresentedEvent, formData: SupportRequestFormData): Observable<SupportRequest> {
    // Get next person in roster rotation
    const { fe, be } = this.getNextInRotation()

    const assignedTo = this._getAssignedEngineers(formData.type, fe, be)

    // Create support request object
    const supportRequest: SupportRequest = {
      id: this.generateRequestId(),
      type: formData.type,
      description: formData.description,
      personas: formData.personas.length > 0 ? formData.personas : undefined,
      personaNames: formData.fields.map((f) => f.personaName),
      fields: formData.fields.length > 0 ? formData.fields : undefined,
      fulfilled: false,
      createdAt: new Date().toISOString(),
      createdBy: {
        name: this.authService.user.full_name,
        email: this.authService.user.email
      },
      slackMessageSent: false,
      assignedTo: assignedTo.map((engineer) => engineer.name).join(', ')
    }

    // Prepare Slack payload
    const slackPayload: SupportRequestSlackPayload = {
      eventName: event.name,
      eventSlug: event.slug,
      workspaceName: event.workspace?.name || this.authService.currentWorkspace?.name || '',
      workspaceSlug: event.workspace?.slug || this.authService.currentWorkspace?.slug || '',
      requestType: formData.type,
      personaNames: formData.personaNames.length > 0 ? formData.personaNames : undefined,
      fields: formData.fields.length > 0 ? formData.fields : undefined,
      description: formData.description,
      assignedTo: assignedTo.map((engineer) => engineer.slackId),
      createdBy: supportRequest.createdBy
    }

    // Send Slack message and update event in parallel
    return forkJoin({
      slack: this.integrationService.sendSupportRequest(slackPayload).pipe(
        map(() => true),
        catchError(() => of(false))
      ),
      event: this.addSupportRequestToEvent(event, supportRequest)
    }).pipe(
      map(({ slack }) => {
        supportRequest.slackMessageSent = slack
        return supportRequest
      })
    )
  }

  /**
   * Get the assigned engineers for a support request
   * @param type Support request type
   * @param feAssignee Frontend roster member
   * @param beAssignee Backend roster member
   * @returns Array of assigned engineers
   */
  private _getAssignedEngineers(type: SupportRequestType, feAssignee: RosterMember, beAssignee: RosterMember): RosterMember[] {
    const assignedTo: RosterMember[] = []
    if (type === SupportRequestType.THIRD_PARTY_APPS) return INTEGRATION_TEAM
    if (beAssignee && BackendRequestTypes.includes(type)) assignedTo.push(beAssignee)
    if (feAssignee && FrontendRequestTypes.includes(type)) assignedTo.push(feAssignee)
    return assignedTo.length > 0 ? assignedTo : [feAssignee, beAssignee]
  }

  /**
   * Updates the fulfilled status of a support request
   * @param event Event object
   * @param requestId Support request ID
   * @param fulfilled New fulfilled status
   * @returns Observable<ServerRepresentedEvent>
   */
  updateFulfilledStatus(event: ServerRepresentedEvent, requestId: string, fulfilled: boolean): Observable<ServerRepresentedEvent> {
    return this.updateSupportRequestFulfilled(event, requestId, fulfilled)
  }

  /**
   * Adds a support request to an event's options
   * @param event The event object (avoids unnecessary API call)
   * @param supportRequest Support request to add
   * @returns Observable<ServerRepresentedEvent>
   */
  private addSupportRequestToEvent(event: ServerRepresentedEvent, supportRequest: SupportRequest): Observable<ServerRepresentedEvent> {
    const currentOptions = event.options || {}
    const currentRequests = currentOptions.supportRequests || []

    const updatedOptions = {
      ...currentOptions,
      supportRequests: [...currentRequests, supportRequest]
    }

    return this.eventService.updateEvent(event.id, { options: updatedOptions })
  }

  /**
   * Updates the fulfilled status of a support request within event options
   * @param event The event object (avoids unnecessary API call)
   * @param requestId Support request ID
   * @param fulfilled New fulfilled status
   * @returns Observable<ServerRepresentedEvent>
   */
  private updateSupportRequestFulfilled(event: ServerRepresentedEvent, requestId: string, fulfilled: boolean): Observable<ServerRepresentedEvent> {
    const currentOptions = event.options || {}
    const currentRequests = currentOptions.supportRequests || []

    const updatedRequests = currentRequests.map((request: SupportRequest) => (request.id === requestId ? { ...request, fulfilled } : request))

    const updatedOptions = {
      ...currentOptions,
      supportRequests: updatedRequests
    }

    return this.eventService.updateEvent(event.id, { options: updatedOptions })
  }

  /**
   * Get the next person in time-based rotation
   * Uses day of year to determine assignment - same person for all requests on the same day
   * Rotates automatically each day for predictable, fair distribution
   */
  private getNextInRotation(): { fe: RosterMember; be: RosterMember } {
    // Calculate day of year (0-365)
    const now = new Date()
    const startOfYear = new Date(now.getFullYear(), 0, 0)
    const diff = now.getTime() - startOfYear.getTime()
    const dayOfYear = Math.floor(diff / 86400000) // 86400000 ms = 1 day

    // Use day of year to select team members
    const fe = FE_ROSTER[dayOfYear % FE_ROSTER.length]
    const be = BE_ROSTER[dayOfYear % BE_ROSTER.length]

    return { fe, be }
  }

  /**
   * Generates a unique ID for a support request
   * @returns string
   */
  private generateRequestId(): string {
    return `sr-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }
}
